import { useState, useMemo, useCallback } from 'react';
import { toast } from 'react-toastify';
import { FUNCTIONAL_SAFETY_CATEGORY, FUNCTIONAL_SAFETY_CHANNEL_TYPES, getDcavgCategory, hsrToDcEquivalent } from '../utils/constants';

const CHANNEL_TYPES = FUNCTIONAL_SAFETY_CHANNEL_TYPES;

// Categories that should show TE and OTE channels for DCavg
// Category 2 should NOT show TE/OTE for DCavg (per document: "For category 2 only, the only parts considered are the full channel")
const CATEGORIES_WITH_TE_OTE = [
  FUNCTIONAL_SAFETY_CATEGORY.CATEGORY_2M,
//   FUNCTIONAL_SAFETY_CATEGORY.CATEGORY_3,
//   FUNCTIONAL_SAFETY_CATEGORY.CATEGORY_4,
];

// Categories that should show Channel 2 (INPUT2, LOGIC2, OUTPUT2)
const CATEGORIES_WITH_CHANNEL_2 = [
  FUNCTIONAL_SAFETY_CATEGORY.CATEGORY_3,
  FUNCTIONAL_SAFETY_CATEGORY.CATEGORY_4,
];

/**
 * Custom hook for DCavg component logic
 * Handles state management, calculations, and data organization
 */
const useDCavg = ({
  assessment,
  groupedMTTFData,
  dcavgAssessments,
  hsrAssessments,
  createDCavg,
  updateDCavg,
  deleteDCavg,
  isCreating,
  isUpdating,
  isDeleting,
}) => {
  const [newRows, setNewRows] = useState({
    input: [],
    logic: [],
    output: [],
    te: [],
    ote: [],
    input2: [],
    logic2: [],
    output2: [],
  });

  const [editingRow, setEditingRow] = useState(null);
  const [editedData, setEditedData] = useState({});

  // Check if TE and OTE should be shown based on category
  const shouldShowTeOte = useMemo(() => {
    return assessment?.category && CATEGORIES_WITH_TE_OTE.includes(assessment.category);
  }, [assessment?.category]);

  // Check if Channel 2 should be shown based on category
  const shouldShowChannel2 = useMemo(() => {
    return assessment?.category && CATEGORIES_WITH_CHANNEL_2.includes(assessment.category);
  }, [assessment?.category]);

  // Find DCavg assessment for a given MTTF ID
  const findDCavgForMTTF = useCallback((mttfId) => {
    return dcavgAssessments.find(dc => dc.mttf_assessment === mttfId);
  }, [dcavgAssessments]);

  // Find new row for a given MTTF ID
  const findNewRowForMTTF = useCallback((type, mttfId) => {
    return newRows[type].find(row => row.mttfId === mttfId);
  }, [newRows]);

  // Start editing a row (defined early for use in addDCForMTTF)
  const handleStartEdit = useCallback((type, mttfItem, dcItem) => {
    setEditingRow({type, id: dcItem.id, mttfId: mttfItem.id});
    setEditedData({
      ...dcItem,
      dc: dcItem.dc * 100,
      note: dcItem.note || '',
    });
  }, []);

  // Add DC for an MTTF component
  const addDCForMTTF = useCallback((type, mttfItem) => {
    const existingDC = findDCavgForMTTF(mttfItem.id);
    if (existingDC) {
      handleStartEdit(type, mttfItem, existingDC);
      return;
    }

    const existingNew = findNewRowForMTTF(type, mttfItem.id);
    if (existingNew) return;

    const newRow = {
      tempId: `temp_${Date.now()}`,
      mttfId: mttfItem.id,
      dc: '',
      note: '',
    };

    setNewRows(prev => ({
      ...prev,
      [type]: [...prev[type], newRow],
    }));
  }, [findDCavgForMTTF, findNewRowForMTTF, handleStartEdit]);

  // Handle new row field changes
  const handleNewRowChange = useCallback((type, tempId, field, value) => {
    setNewRows(prev => ({
      ...prev,
      [type]: prev[type].map(row =>
        row.tempId === tempId ? {...row, [field]: value} : row
      ),
    }));
  }, []);

  // Remove new row
  const handleRemoveNewRow = useCallback((type, tempId) => {
    setNewRows(prev => ({
      ...prev,
      [type]: prev[type].filter(r => r.tempId !== tempId),
    }));
  }, []);

  // Save all new rows
  const handleSaveAllNewRows = useCallback(async () => {
    // Check if MTTF assessments exist (dependency validation)
    const hasMTTFData = groupedMTTFData && (
      (groupedMTTFData.input && groupedMTTFData.input.length > 0) ||
      (groupedMTTFData.logic && groupedMTTFData.logic.length > 0) ||
      (groupedMTTFData.output && groupedMTTFData.output.length > 0) ||
      (groupedMTTFData.te && groupedMTTFData.te.length > 0) ||
      (groupedMTTFData.ote && groupedMTTFData.ote.length > 0) ||
      (groupedMTTFData.input2 && groupedMTTFData.input2.length > 0) ||
      (groupedMTTFData.logic2 && groupedMTTFData.logic2.length > 0) ||
      (groupedMTTFData.output2 && groupedMTTFData.output2.length > 0)
    );

    if (!hasMTTFData) {
      toast.error('Please create MTTF assessments before adding DCavg values.', {
        autoClose: 3000,
        className: 'toast-error',
      });
      return;
    }

    const allNewRows = [
      ...newRows.input,
      ...newRows.logic,
      ...newRows.output,
      ...newRows.te,
      ...newRows.ote,
      ...newRows.input2,
      ...newRows.logic2,
      ...newRows.output2,
    ];

    if (allNewRows.length === 0) {
      return;
    }

    const hasEmptyFields = allNewRows.some(row => !row.dc || row.dc === '');

    if (hasEmptyFields) {
      toast.error('Please fill in all DC values before saving.', {
        autoClose: 2000,
        className: 'toast-error',
      });
      return;
    }

    const payloads = allNewRows.map(row => ({
      mttf_assessment: row.mttfId,
      dc: parseFloat(row.dc) / 100,
      note: row.note || null,
    }));

    await createDCavg(payloads);

    setNewRows({
      input: [],
      logic: [],
      output: [],
      te: [],
      ote: [],
      input2: [],
      logic2: [],
      output2: [],
    });
  }, [newRows, createDCavg, groupedMTTFData]);

  // Check if there are unsaved rows
  const hasUnsavedRows = useMemo(() => {
    return (
      newRows.input.length > 0 ||
      newRows.logic.length > 0 ||
      newRows.output.length > 0 ||
      newRows.te.length > 0 ||
      newRows.ote.length > 0 ||
      newRows.input2.length > 0 ||
      newRows.logic2.length > 0 ||
      newRows.output2.length > 0
    );
  }, [newRows]);


  // Handle edit field changes
  const handleEditChange = useCallback((field, value) => {
    setEditedData(prev => ({...prev, [field]: value}));
  }, []);

  // Save edited row
  const handleSaveEdit = useCallback(async () => {
    if (!editingRow) return;

    const payload = {
      mttf_assessment: editingRow.mttfId,
      dc: parseFloat(editedData.dc) / 100,
      note: editedData.note || null,
    };

    await updateDCavg(editingRow.id, payload);
    setEditingRow(null);
    setEditedData({});
  }, [editingRow, editedData, updateDCavg]);

  // Cancel editing
  const handleCancelEdit = useCallback(() => {
    setEditingRow(null);
    setEditedData({});
  }, []);

  // Delete a row
  const handleDelete = useCallback(async (id) => {
    await deleteDCavg(id);
  }, [deleteDCavg]);

  // Calculate weighted DCavg for a channel using the formula:
  // DC_avg = Σ(DC_i / MTTF_Di * quantity_i) / Σ(1 / MTTF_Di * quantity_i)
  // Where:
  // - DC_i: Diagnostic Coverage for component i (as decimal 0-1)
  // - MTTF_Di: Mean Time To Dangerous Failure for component i (mttfdij)
  // - quantity_i: Quantity of component i
  const calculateChannelDCavg = useCallback((types) => {
    let numerator = 0;
    let denominator = 0;

    types.forEach(type => {
      const mttfItems = groupedMTTFData?.[type] || [];
      
      mttfItems.forEach(mttfItem => {
        const existingDC = findDCavgForMTTF(mttfItem.id);
        const newDC = findNewRowForMTTF(type, mttfItem.id);

        // Get DC value (as decimal 0-1)
        let dcValue = null;
        if (existingDC) {
          dcValue = Number(existingDC.dc); // Already stored as decimal 0-1
        } else if (newDC && newDC.dc) {
          dcValue = Number(newDC.dc) / 100; // Convert from percentage to decimal
        }

        // Get MTTF_D (mttfdij) and quantity
        const mttfdij = Number(mttfItem.mttfdij) || 0;
        const quantity = Number(mttfItem.quantity) || 0;
        

        // Only include components that have both DC and valid MTTF_D
        if (dcValue !== null && mttfdij > 0 && quantity > 0) {
          // Numerator: DC_i / MTTF_Di * quantity_i
          numerator += (dcValue / mttfdij) * quantity;
          
          // Denominator: 1 / MTTF_Di * quantity_i
          denominator += (1 / mttfdij) * quantity;
        }
      });
    });

    // DC_avg = numerator / denominator
    return denominator > 0 ? numerator / denominator : 0;
  }, [groupedMTTFData, findDCavgForMTTF, findNewRowForMTTF]);

  // Calculate HSR score if HSR method is enabled
  const hsrScore = useMemo(() => {
    if (!assessment?.hsr_method || !hsrAssessments) return 0;
    return hsrAssessments.reduce((sum, hsr) => sum + (hsr.hsr_option?.score || 0), 0);
  }, [assessment?.hsr_method, hsrAssessments]);

  // Calculate HSR equivalent DCavg
  const hsrEquivalentDCavg = useMemo(() => {
    if (!assessment?.hsr_method) return null;
    const equivalent = hsrToDcEquivalent(hsrScore);
    return {
      value: equivalent,
      category: getDcavgCategory(equivalent),
      isFromHSR: true,
    };
  }, [assessment?.hsr_method, hsrScore]);

  // Channel 1 DCavg calculation (Input, Logic, Output only)
  // Using weighted average formula: DC_avg = Σ(DC_i / MTTF_Di * quantity_i) / Σ(1 / MTTF_Di * quantity_i)
  // If HSR method is enabled, use HSR equivalent instead
  const dcAvgChannel1 = useMemo(() => {
    if (assessment?.hsr_method && hsrEquivalentDCavg) {
      return hsrEquivalentDCavg;
    }
    const value = calculateChannelDCavg(['input', 'logic', 'output']);
    // Convert to percentage for display (0-100%)
    // Round to 2 decimal places to avoid floating-point precision issues
    const percentageValue = Math.round((value * 100) * 100) / 100;
    const category = getDcavgCategory(percentageValue);
    return { value: percentageValue, category, isFromHSR: false };
  }, [calculateChannelDCavg, assessment?.hsr_method, hsrEquivalentDCavg]);

  // Half Channel DCavg calculation (TE, OTE)
  // Using weighted average formula: DC_avg = Σ(DC_i / MTTF_Di * quantity_i) / Σ(1 / MTTF_Di * quantity_i)
  const dcAvgHalfChannel = useMemo(() => {
    if (!shouldShowTeOte) {
      return { value: 0, category: null };
    }
    const value = calculateChannelDCavg(['te', 'ote']);
    // Convert to percentage for display (0-100%)
    // Round to 2 decimal places to avoid floating-point precision issues
    const percentageValue = Math.round((value * 100) * 100) / 100;
    const category = getDcavgCategory(percentageValue);
    return { value: percentageValue, category };
  }, [calculateChannelDCavg, shouldShowTeOte]);

  // Channel 2 DCavg calculation (separate from Channel 1)
  // Using weighted average formula: DC_avg = Σ(DC_i / MTTF_Di * quantity_i) / Σ(1 / MTTF_Di * quantity_i)
  const dcAvgChannel2 = useMemo(() => {
    if (!shouldShowChannel2) {
      return { value: 0, category: null };
    }
    const value = calculateChannelDCavg(['input2', 'logic2', 'output2']);
    // Convert to percentage for display (0-100%)
    // Round to 2 decimal places to avoid floating-point precision issues
    const percentageValue = Math.round((value * 100) * 100) / 100;
    const category = getDcavgCategory(percentageValue);
    return { value: percentageValue, category };
  }, [calculateChannelDCavg, shouldShowChannel2]);

  // Final DCavg calculation across ALL channels using weighted average
  // Formula: Final DCavg = Σ(DC_i / MTTF_Di) / Σ(1 / MTTF_Di)
  // This combines all channels (Channel 1, Channel 2, Half Channel) into one calculation
  // For each component: dcSum += dc/mttf, mttfSum += 1/mttf
  const finalDCavg = useMemo(() => {
    let dcSum = 0;  // Sum of (dc/mttf) for all components
    let mttfSum = 0;  // Sum of (1/mttf) for all components

    // Collect all channel types that should be included
    const allChannelTypes = ['input', 'logic', 'output'];
    
    // Add Channel 2 if applicable
    if (shouldShowChannel2) {
      allChannelTypes.push('input2', 'logic2', 'output2');
    }
    
    // Add Half Channel (TE, OTE) if applicable
    if (shouldShowTeOte) {
      allChannelTypes.push('te', 'ote');
    }

    // Calculate across all channels
    allChannelTypes.forEach(type => {
      const mttfItems = groupedMTTFData?.[type] || [];
      
      mttfItems.forEach(mttfItem => {
        const existingDC = findDCavgForMTTF(mttfItem.id);
        const newDC = findNewRowForMTTF(type, mttfItem.id);

        // Get DC value (as decimal 0-1)
        let dcValue = null;
        if (existingDC) {
          dcValue = Number(existingDC.dc); // Already stored as decimal 0-1
        } else if (newDC && newDC.dc) {
           dcValue = Number(newDC.dc) / 100; // Convert from percentage to decimal
        }

        // Get MTTF_D (mttfdij)
        const mttfdij = Number(mttfItem.mttfdij) || 0;

        // Only include components that have both DC and valid MTTF_D
        if (dcValue !== null && mttfdij > 0) {
          // Add to dcSum: dc/mttf
          dcSum += dcValue / mttfdij;
          
          // Add to mttfSum: 1/mttf
          mttfSum += 1 / mttfdij;
        }
      });
    });

    // Final DCavg = dcSum / mttfSum
    if (mttfSum > 0) {
      const value = dcSum / mttfSum;
      // Convert to percentage for display (0-100%)
      // Round to 2 decimal places to avoid floating-point precision issues
      const percentageValue = Math.round((value * 100) * 100) / 100;
      const category = getDcavgCategory(percentageValue);
      return { value: percentageValue, category };
    }

    // No valid components
    return { value: 0, category: null };
  }, [groupedMTTFData, findDCavgForMTTF, findNewRowForMTTF, shouldShowChannel2, shouldShowTeOte]);


  return {
    // State
    newRows,
    editingRow,
    editedData,

    // Flags
    shouldShowTeOte,
    shouldShowChannel2,
    hasUnsavedRows,

    // Calculations
    dcAvgChannel1,
    dcAvgHalfChannel,
    dcAvgChannel2,
    finalDCavg,
    hsrEquivalentDCavg,
    hsrScore,

    // Helper functions
    findDCavgForMTTF,
    findNewRowForMTTF,

    // Actions
    addDCForMTTF,
    handleNewRowChange,
    handleRemoveNewRow,
    handleSaveAllNewRows,
    handleStartEdit,
    handleEditChange,
    handleSaveEdit,
    handleCancelEdit,
    handleDelete,

    // Loading states
    isCreating,
    isUpdating,
    isDeleting,
  };
};

export default useDCavg;

